import { ITreeNodeInstance } from '@infotech-soe/merps/types/Tree';
import { TDatabaseId, tg } from '@infotech-soe/metaschema';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import cloneDeep from 'lodash/cloneDeep';

import { CategoryTreeStateEntity } from '../../models/category/categoryTree';
import UniqueId from '../../services/uid/UniqueId';

import { failureAction, pendingAction, successAction } from './actionHandler';
import { CategoryTargetingInfo } from './category';
import { CategoryTree } from './dm-trees/CategoryTree';
import { CategoryTreeNode } from './dm-trees/CategoryTreeNode';

export type CatalogsStateEntity = CategoryTreeStateEntity;
export type CatalogsState = Record<UniqueId, CatalogsStateEntity>;

const getTreeFor = (state: CatalogsState, catalogId: UniqueId): CategoryTree =>
  new CategoryTree(state[catalogId]);

const catalogsSlice = createSlice({
  name: 'catalogs',
  initialState: {} as CatalogsState,
  reducers: {
    initializeTree(
      state,
      { payload: { catalogId } }: PayloadAction<{ catalogId: UniqueId }>
    ) {
      state[catalogId] = {
        categoryId: catalogId,
        nodes: [],
        activeId: null,
        hierarchyRootId: null,
        checkedNodes: ImmutableSet(),
        expandedNodes: ImmutableSet(),
        pendingNodes: ImmutableSet(),
        childrenCache: ImmutableMap(),
      };
    },
    cloneTree(
      state,
      {
        payload: { catalogId, targetId },
      }: PayloadAction<{ catalogId: UniqueId; targetId: UniqueId }>
    ) {
      const tree = (state[targetId] = cloneDeep(state[catalogId]));

      if (tree.activeId === null) {
        const rootNode = tree.nodes.find(({ parentId }) => parentId === null);
        if (tg.isDefined(rootNode?.id)) tree.activeId = rootNode.id;
      }

      tree.pendingNodes = ImmutableSet();
      tree.hierarchyRootId = null;
    },
    destroyTree(
      state,
      { payload: { catalogId } }: PayloadAction<{ catalogId: UniqueId }>
    ) {
      delete state[catalogId];
    },
    loadRoots: pendingAction<
      CatalogsState,
      {
        catalogId: UniqueId;
        targetingInfo?: CategoryTargetingInfo;
        forceDOMFocus?: true;
      }
    >(),
    loadRootsSuccess: successAction<
      CatalogsState,
      {
        catalogId: UniqueId;
        nodes: ITreeNodeInstance[];
        targetingInfo?: CategoryTargetingInfo;
        forceDOMFocus?: true;
      }
    >((state, { payload: { catalogId, nodes } }) => {
      getTreeFor(state, catalogId).setRoots(nodes);
    }),
    loadRootsFailure: failureAction(),
    expandNode(
      state,
      {
        payload: { catalogId, nodeId },
      }: PayloadAction<{ catalogId: UniqueId; nodeId?: TDatabaseId }>
    ) {
      const tree = getTreeFor(state, catalogId);
      if (nodeId !== undefined) {
        tree.expandNode(nodeId);
      } else {
        tree.expandActiveNode();
      }
    },
    collapseNode(
      state,
      {
        payload: { catalogId, nodeId },
      }: PayloadAction<{ catalogId: UniqueId; nodeId?: TDatabaseId }>
    ) {
      const tree = getTreeFor(state, catalogId);
      if (nodeId !== undefined) {
        tree.collapseNode(nodeId);
      } else {
        tree.collapseActiveNode();
      }
    },
    markNodePending(
      state,
      {
        payload: { catalogId, nodeId },
      }: PayloadAction<{ catalogId: UniqueId; nodeId: TDatabaseId }>
    ) {
      getTreeFor(state, catalogId).markPending(nodeId);
    },
    unmarkNodePending(
      state,
      {
        payload: { catalogId, nodeId },
      }: PayloadAction<{ catalogId: UniqueId; nodeId: TDatabaseId }>
    ) {
      getTreeFor(state, catalogId).unmarkPending(nodeId);
    },
    loadChildren: pendingAction<
      CatalogsState,
      { catalogId: UniqueId; parentNodeId: TDatabaseId }
    >(),
    loadChildrenSuccess: successAction<
      CatalogsState,
      {
        catalogId: UniqueId;
        parentNodeId: TDatabaseId;
        nodes: ITreeNodeInstance[];
      }
    >((state, { payload: { catalogId, parentNodeId, nodes } }) => {
      getTreeFor(state, catalogId).setChildren(parentNodeId, nodes);
    }),
    loadChildrenFailure: failureAction(),
    selectNode(
      state,
      {
        payload: { catalogId, nodeId },
      }: PayloadAction<{
        catalogId: UniqueId;
        nodeId: TDatabaseId;
        targetingInfo?: CategoryTargetingInfo;
        forceDOMFocus?: true;
        fullHierarchy?: boolean;
      }>
    ) {
      getTreeFor(state, catalogId).activateNode(nodeId);
    },
    toggleCheck: pendingAction<
      CatalogsState,
      { catalogId: UniqueId; nodeId?: TDatabaseId; shift?: boolean }
    >(),
    toggleCheckSuccess: successAction<
      CatalogsState,
      { catalogId: UniqueId; nodeId?: TDatabaseId; shift?: boolean }
    >(),
    toggleCheckFailure: failureAction(),
    toggleNodeChecked(
      state,
      {
        payload: { catalogId, parentId, nodeIds },
      }: PayloadAction<{
        catalogId: UniqueId;
        parentId: TDatabaseId;
        nodeIds: TDatabaseId[];
      }>
    ) {
      const tree = getTreeFor(state, catalogId);
      tree.toggleNodeChecks(parentId, nodeIds);
      tree.activateNode(nodeIds[0]);
    },
    nextNode(
      state,
      { payload: { catalogId } }: PayloadAction<{ catalogId: UniqueId }>
    ) {
      getTreeFor(state, catalogId).activateNext();
    },
    prevNode(
      state,
      { payload: { catalogId } }: PayloadAction<{ catalogId: UniqueId }>
    ) {
      getTreeFor(state, catalogId).activatePrevious();
    },
    setHierarchy(
      state,
      {
        payload: { catalogId, nodeId },
      }: PayloadAction<{
        catalogId: UniqueId;
        nodeId?: TDatabaseId;
      }>
    ) {
      const tree = getTreeFor(state, catalogId);
      if (nodeId !== undefined) {
        tree.setHierarchyRoot(nodeId);
      } else {
        tree.setActiveAsHierarchyRoot();
      }
    },
    clearChecks(
      state,
      { payload: { catalogId } }: PayloadAction<{ catalogId: UniqueId }>
    ) {
      getTreeFor(state, catalogId).clearChecksAndHierarchy();
    },
    restoreChecks(
      state,
      {
        payload: { catalogId, nodeIds },
      }: PayloadAction<{ catalogId: UniqueId; nodeIds: TDatabaseId[] }>
    ) {
      const tree = getTreeFor(state, catalogId);
      nodeIds.forEach(id => tree.setNodeChecked(id));
    },
    locateNode: pendingAction<
      CatalogsState,
      {
        catalogId: UniqueId;
        nodeId: TDatabaseId;
        treeId: TDatabaseId;
        targetingInfo?: CategoryTargetingInfo;
      }
    >(),
    locateNodeSuccess: successAction<
      CatalogsState,
      {
        catalogId: UniqueId;
        nodeId: TDatabaseId;
        roots: ITreeNodeInstance[];
        path: Array<[TDatabaseId, ITreeNodeInstance[]]>;
        treeId: TDatabaseId;
        targetingInfo?: CategoryTargetingInfo;
      }
    >((state, { payload: { catalogId, nodeId, roots, path } }) => {
      const tree = getTreeFor(state, catalogId);
      tree.setRoots(roots);

      for (const [pathNodeId, children] of path) {
        tree.expandNode(pathNodeId);
        tree.setChildren(pathNodeId, children);
      }

      tree.activateNode(nodeId);
    }),
    locateNodeFailure: failureAction(),
    refresh: pendingAction<
      CatalogsState,
      { catalogId: UniqueId; loadPathToNode?: TDatabaseId }
    >(),
    refreshSuccess: successAction<
      CatalogsState,
      {
        catalogId: UniqueId;
        hierarchyRootId?: TDatabaseId | null;
        isExpandedNode?: boolean;
      }
    >((state, { payload: { catalogId, hierarchyRootId, isExpandedNode } }) => {
      state[catalogId].refreshHierarchyRootId = hierarchyRootId;
      state[catalogId].refreshIsExpandedNode = isExpandedNode;
    }),
    refreshFailure: failureAction(),
    hierarchyToChecks: pendingAction<
      CatalogsState,
      { catalogId: UniqueId; excludedNodes: TDatabaseId[] }
    >(),
    hierarchyToChecksSuccess: successAction<
      CatalogsState,
      {
        catalogId: UniqueId;
        checkedNodes: TDatabaseId[];
        excludedNodes: TDatabaseId[];
      }
    >((state, { payload: { catalogId, checkedNodes } }) => {
      const tree = getTreeFor(state, catalogId);
      tree.unsetHierarchyRoot();
      tree.toggleNodeChecks(tree.getActiveNode().id, checkedNodes);
    }),
    hierarchyToChecksFailure: failureAction(),
    create: pendingAction<
      CatalogsState,
      { catalogId: UniqueId; parentNodeId: TDatabaseId; name: string }
    >(),
    createSuccess: successAction<
      CatalogsState,
      {
        catalogId: UniqueId;
        parentNodeId: TDatabaseId;
        nodeId: TDatabaseId;
        name: string;
      }
    >((state, { payload: { catalogId, parentNodeId, nodeId, name } }) => {
      getTreeFor(state, catalogId).addNode(
        parentNodeId,
        new CategoryTreeNode(nodeId, name)
      );
    }),
    createFailure: failureAction(),
    rename: pendingAction<
      CatalogsState,
      { catalogId: UniqueId; nodeId: TDatabaseId; name: string }
    >(),
    renameSuccess: successAction<
      CatalogsState,
      { catalogId: UniqueId; nodeId: TDatabaseId; name: string }
    >((state, { payload: { catalogId, nodeId, name } }) => {
      getTreeFor(state, catalogId).renameNode(nodeId, name);
    }),
    renameFailure: failureAction(),
    delete: pendingAction<
      CatalogsState,
      { catalogId: UniqueId; nodeId: TDatabaseId }
    >(),
    deleteSuccess: successAction<
      CatalogsState,
      { catalogId: UniqueId; nodeId: TDatabaseId }
    >((state, { payload: { catalogId, nodeId } }) => {
      getTreeFor(state, catalogId).deleteNode(nodeId);
    }),
    deleteFailure: failureAction(),
    move: pendingAction<
      CatalogsState,
      { catalogId: UniqueId; nodeId: TDatabaseId; newParentId: TDatabaseId }
    >(),
    moveSuccess: successAction<
      CatalogsState,
      { catalogId: UniqueId; nodeId: TDatabaseId; newParentId: TDatabaseId }
    >((state, { payload: { catalogId, nodeId, newParentId } }) => {
      getTreeFor(state, catalogId).moveNode(nodeId, newParentId);
    }),
    moveFailure: failureAction(),
    moveIn: pendingAction<
      CatalogsState,
      { catalogId: UniqueId; moveIds: TDatabaseId[]; newParentId: TDatabaseId }
    >(),
    moveInSuccess: successAction<
      CatalogsState,
      { catalogId: UniqueId; moveIds: TDatabaseId[] }
    >(),
    moveInFailure: failureAction(),
    disableNode(
      state,
      {
        payload: { catalogId, nodeId },
      }: PayloadAction<{ catalogId: UniqueId; nodeId: TDatabaseId }>
    ) {
      getTreeFor(state, catalogId).disableNode(nodeId);
    },
    linkWithForm(
      state,
      {
        payload: { catalogId, formId },
      }: PayloadAction<{ catalogId: UniqueId; formId: UniqueId }>
    ) {
      state[catalogId].formId = formId;
    },
  },
});

export const catalogsReducer = catalogsSlice.reducer;

export default catalogsSlice.actions;
